import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useInView } from 'react-intersection-observer';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { API } from 'core/API';
import { INITIAL_PAGINATION } from 'core/constants';
import { setSearchOrSortQueryString } from 'core/functions';
import { resolveNextSort } from 'core/functions/sort';

import MenuTableWithFilter from 'components/Organization/TableMenuWithFilter';
import MenuTable from 'components/Organization/TableMenu';
import Loader from 'shared/components/Loader';
import ResponseFailure from 'shared/components/ResponseFailure';

import TableBody from 'shared/components/Table/TableBody';
import TableHead from 'shared/components/Table/TableHead';
import TableMain from 'shared/components/Table/TableMain';
import TableRow from 'shared/components/Table/TableRow';

import {
	ADMIN_SEARCH_FILTER_TYPE,
	IOpportunityResponse,
	ISearchOpportunitiesSort,
	OPPORTUNITY_ACTIVE_STATUS,
	OPPORTUNITY_STATUSES,
	SEARCH_OPPORTUNITY_POSSIBLE_SEARCH,
	SEARCH_OPPORTUNITIES_POSSIBLE_SORT,
	SearchChallengesRequest,
	SearchOpportunitiesSort,
	SearchChallengeResponse,
	SortDirection,
} from '@joc/api-gateway';

import { EditManualOppPopup } from 'components/Organization/Opportunities/ManualOpportunityForm/EditMannualOppoPopup/EditManualOppoPopup';
import PopupSoloOppoDetails from 'components/Organization/Opportunities/OpportunityPopups/PopupSoloOppoDetails';

import { SUPER_ADMIN_OPPORTUNITIES_QUERY_KEY, useSuperAdminOpportunities } from './hooks/useSuperAdminOpportunities';
import { SuperAdminOpportunitiesFilters } from './mappers';
import { getTableConfig } from './table/getTableConfig';
import SuperAdminOpportunitiesActionsNew from './SuperAdminOpportunitiesActionsNew';
import { OppoContext } from 'shared/components/OppListContent';
import SuperAdminOpportunitiesSidebarNew from './SuperAdminOpportunitiesSidebarNew';
import { applyTabRecordsRules } from './helpers/records';
import {
	parseOrganisationIds,
	parseQuery,
	parseSearchText,
	parseSortFromQuery,
	parseStartDayFromQuery,
	stringifyQuery,
} from './helpers/queryParams';
import SuperAdminPostsTableNew from './SuperAdminPostsTableNew';

import styles from 'components/Organization/Opportunities/Table/index.module.scss';

const customErrorMessage = 'We couldn`t find the Chesed opportunities you were looking for this time.';

type SuperAdminOpportunitiesTableNewProps = {
	opportunityFilterType?: ADMIN_SEARCH_FILTER_TYPE;
};

type SuperAdminOpportunitiesTableContentProps = {
	opportunityFilterType: ADMIN_SEARCH_FILTER_TYPE;
};

const SuperAdminOpportunitiesTableContent: FC<SuperAdminOpportunitiesTableContentProps> = ({
	opportunityFilterType,
}: SuperAdminOpportunitiesTableContentProps) => {
	const history = useHistory();
	const location = useLocation();
	const queryClient = useQueryClient();
	// @ts-ignore
	const { isClearSearch, setIsClearSearch } = useContext(OppoContext);

	const [sidebarVisibility, setSidebarVisibility] = useState(false);
	const changeVisibilityFilters = () => setSidebarVisibility(prev => !prev);
	const handleCloseFilters = () => setSidebarVisibility(false);

	const [sortLocal, setSortLocal] = useState<SearchOpportunitiesSort | undefined>(undefined);
	const [isPrintableTable, setIsPrintableTable] = useState(false);
	const printRef = useRef<HTMLDivElement>(null);

	const [oppoToEdit, setOppoToEdit] = useState<IOpportunityResponse | undefined>(undefined);
	const [oppoToView, setOppoToView] = useState<IOpportunityResponse | undefined>(undefined);

	const parsedQuery = useMemo(() => parseQuery(location.search), [location.search]);
	const searchText: string | undefined = useMemo(() => parseSearchText(parsedQuery), [parsedQuery]);

	useEffect(() => {
		setSortLocal(parseSortFromQuery(parsedQuery));
	}, [location.search]); // keep legacy behavior (URL is source of truth)

	const organisationIds: Array<number> | undefined = useMemo(
		() => parseOrganisationIds(parsedQuery),

		[parsedQuery]
	);

	const startDayFromUrl = useMemo(() => parseStartDayFromQuery(parsedQuery), [parsedQuery]);

	const startDay = useMemo(() => {
		if (opportunityFilterType === ADMIN_SEARCH_FILTER_TYPE.UPCOMING && !startDayFromUrl) {
			// Legacy UPCOMING preset starts "from now".
			return { fromDate: new Date(), toDate: undefined };
		}
		return startDayFromUrl;
	}, [opportunityFilterType, startDayFromUrl]);

	const filters: SuperAdminOpportunitiesFilters = useMemo(
		() => ({
			startDay,
			organisationIds,
		}),
		[startDay, organisationIds]
	);

	const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage, refetch, error } =
		useSuperAdminOpportunities({
			opportunityFilterType,
			search: searchText,
			sort: sortLocal,
			filters,
			pagination: { take: INITIAL_PAGINATION.take },
		});

	const opportunitiesRecords = useMemo(() => {
		const all = (data?.pages?.flatMap((page: any) => page.records) || []) as Array<IOpportunityResponse>;
		return applyTabRecordsRules(all, opportunityFilterType);
	}, [data, opportunityFilterType]);

	const { ref: loadMoreRef, inView } = useInView({ threshold: 0, delay: 200 });
	useEffect(() => {
		if (inView && hasNextPage) {
			fetchNextPage().finally();
		}
	}, [inView, hasNextPage, fetchNextPage]);

	const appendQueryString = useCallback(
		(
			newQueryParams: Partial<ISearchOpportunitiesSort> | { value: string; fields: Array<any> },
			searchBy: string
		) => {
			const searchQuery = setSearchOrSortQueryString(location.search, newQueryParams as any, searchBy);
			history.push({ search: searchQuery });
		},
		[history, location.search]
	);

	const descFirstSortFields = useMemo(() => new Set([SEARCH_OPPORTUNITIES_POSSIBLE_SORT.Duration]), []);

	const handleSortChange = useCallback(
		(sortBy: SEARCH_OPPORTUNITIES_POSSIBLE_SORT, clickedDirection?: SortDirection) => {
			setSortLocal(prevSort => {
				const nextSort = resolveNextSort({
					currentSort: prevSort,
					nextSortBy: sortBy,
					clickedDirection,
					descFirstSortFields,
					allowClear: true,
				});

				if (!nextSort) {
					const searchParams = parseQuery(location.search);
					delete searchParams.sort;
					history.replace({ search: stringifyQuery(searchParams) });
					return undefined;
				}

				appendQueryString(nextSort as any, 'sort');
				return SearchOpportunitiesSort.fromJS(nextSort as any);
			});
		},
		[appendQueryString, descFirstSortFields, history, location.search]
	);

	const searchChangeHandler = useCallback(
		(value: string): void => {
			const fullTextSearchParams = {
				value,
				fields: Object.values(SEARCH_OPPORTUNITY_POSSIBLE_SEARCH),
			};

			if (fullTextSearchParams.value.length) {
				appendQueryString(fullTextSearchParams as any, 'fullTextSearch');
			} else {
				const searchParams = parseQuery(location.search);
				delete searchParams.fullTextSearch;
				history.push({ search: stringifyQuery(searchParams) });
			}
		},
		[appendQueryString, history, location.search]
	);

	const getErrorMessage = () => {
		const anyErr = error as any;
		return anyErr?.response?.data?.message || anyErr?.response?.message || anyErr?.message || customErrorMessage;
	};

	const isEmptyState = !isLoading && !opportunitiesRecords.length && !isError;
	const isAnyActiveQuery =
		!!searchText ||
		!!filters?.startDay?.fromDate ||
		!!filters?.startDay?.toDate ||
		!!filters?.organisationIds?.length;

	const showEmptyOrError = (isError && !isLoading) || isEmptyState;

	const tableConfig = useMemo(() => getTableConfig(opportunityFilterType), [opportunityFilterType]);

	// Challenges are required for PopupSoloOppoDetails to show challenge section.
	const { data: challengesResponse } = useQuery<SearchChallengeResponse>({
		queryKey: ['superAdminOpportunitiesChallenges'],
		queryFn: async () => API.searchByAdmin(undefined, SearchChallengesRequest.fromJS({})),
		refetchOnWindowFocus: false,
	});

	const challenges = challengesResponse?.records || [];
	const currentChallenge = useMemo(() => {
		if (oppoToView && oppoToView.challengeId && challenges.length) {
			return challenges.find((ch: any) => ch.id === oppoToView.challengeId);
		}
	}, [challenges, oppoToView]);

	return (
		<div className={styles.container}>
			<div className={styles.table}>
				{opportunityFilterType === ADMIN_SEARCH_FILTER_TYPE.SOLO ||
				opportunityFilterType === ADMIN_SEARCH_FILTER_TYPE.PRIVATE ? (
					<MenuTableWithFilter
						searchChangeHandler={searchChangeHandler}
						changeVisibilityFilters={changeVisibilityFilters}
						isClearSearch={isClearSearch as boolean}
						setIsClearSearch={setIsClearSearch}
					>
						<SuperAdminOpportunitiesActionsNew
							printRef={printRef}
							setIsPrintableTable={setIsPrintableTable}
							opportunityFilterType={opportunityFilterType}
							search={searchText}
							sort={sortLocal}
							filters={filters}
							disabled={!opportunitiesRecords.length}
						/>
					</MenuTableWithFilter>
				) : (
					<MenuTable searchChangeHandler={searchChangeHandler}>
						<SuperAdminOpportunitiesActionsNew
							printRef={printRef}
							setIsPrintableTable={setIsPrintableTable}
							opportunityFilterType={opportunityFilterType}
							search={searchText}
							sort={sortLocal}
							filters={filters}
							disabled={!opportunitiesRecords.length}
						/>
					</MenuTable>
				)}

				{opportunityFilterType === ADMIN_SEARCH_FILTER_TYPE.SOLO ||
				opportunityFilterType === ADMIN_SEARCH_FILTER_TYPE.PRIVATE ? (
					<SuperAdminOpportunitiesSidebarNew
						open={sidebarVisibility}
						onClose={handleCloseFilters}
						enableOrganizationFilter={opportunityFilterType === ADMIN_SEARCH_FILTER_TYPE.SOLO}
					/>
				) : null}

				{showEmptyOrError ? (
					<ResponseFailure
						styleTable={true}
						message={getErrorMessage()}
						buttonTitle="Get all"
						buttonClickHandler={() => refetch()}
						buttonUnshow={isEmptyState && isAnyActiveQuery}
					/>
				) : !opportunitiesRecords.length && isLoading ? (
					<div className={styles.loader}>
						<Loader />
					</div>
				) : (
					<TableMain ref={printRef}>
						<TableHead callChild={tableConfig.callChild}>
							{tableConfig.columns.map(col => {
								if (col.hideOnPrint && isPrintableTable) return null;
								return col.renderHeader({ sortLocal, handleSortChange });
							})}
						</TableHead>

						<TableBody>
							{opportunitiesRecords.map((opportunity: IOpportunityResponse) => {
								const inPastOppoList =
									opportunityFilterType === ADMIN_SEARCH_FILTER_TYPE.PAST ||
									opportunity.status === OPPORTUNITY_STATUSES.CLOSED;

								return (
									<TableRow
										key={opportunity.id}
										callChild={tableConfig.callChild}
										suspend={
											opportunity.opportunityActiveStatus.status ===
											OPPORTUNITY_ACTIVE_STATUS.SUSPENDED
										}
									>
										{tableConfig.columns.map(col => {
											if (col.hideOnPrint && isPrintableTable) return null;
											return col.renderCell({
												opportunity,
												isPrintableTable,
												inPastOppoList,
												opportunityFilterType,
												onEdit: setOppoToEdit,
												onView: setOppoToView,
												sortLocal,
												handleSortChange,
											});
										})}
									</TableRow>
								);
							})}

							{hasNextPage && <div ref={loadMoreRef} style={{ height: 1, visibility: 'hidden' }} />}

							{isFetchingNextPage ? (
								<div className={styles.loader} aria-hidden={true}>
									<Loader />
								</div>
							) : null}
						</TableBody>
					</TableMain>
				)}
			</div>

			{oppoToEdit ? (
				<EditManualOppPopup
					opportunityData={oppoToEdit}
					setIsOpenPopup={() => {
						setOppoToEdit(undefined);
					}}
					generalTabClickHandler={() => {
						queryClient.invalidateQueries({
							queryKey: [SUPER_ADMIN_OPPORTUNITIES_QUERY_KEY, opportunityFilterType],
							exact: false,
						});
						setOppoToEdit(undefined);
					}}
				/>
			) : null}

			{oppoToView ? (
				<PopupSoloOppoDetails
					open={!!oppoToView}
					onClose={() => setOppoToView(undefined)}
					opportunity={oppoToView}
					challenge={currentChallenge}
				/>
			) : null}
		</div>
	);
};

const SuperAdminOpportunitiesTableNew: FC<SuperAdminOpportunitiesTableNewProps> = ({ opportunityFilterType }) => {
	if (!opportunityFilterType) {
		return <SuperAdminPostsTableNew />;
	}

	return <SuperAdminOpportunitiesTableContent opportunityFilterType={opportunityFilterType} />;
};

export default SuperAdminOpportunitiesTableNew;
